import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  Button,
  CssBaseline,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  CircularProgress,
  styled,
  Alert,
  TextField,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import theme from 'theme';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LogoutIcon from '@mui/icons-material/Logout';
import ListSubheader from '@mui/material/ListSubheader';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BookIcon from '@mui/icons-material/Book';
import SecurityIcon from '@mui/icons-material/Security';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ArticleIcon from '@mui/icons-material/Article';
import HotelIcon from '@mui/icons-material/Hotel';
import LinkIcon from '@mui/icons-material/Link';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FolderIcon from '@mui/icons-material/Folder';
import InventoryIcon from '@mui/icons-material/Inventory';
import PaletteIcon from '@mui/icons-material/Palette';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupIcon from '@mui/icons-material/Group';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';

import logo from 'assets/images/logo.svg';
import logoEpilo from 'assets/images/logo-epilo.svg';
import logoSem from 'assets/images/logo-sem.webp';
import AppBar from 'components/AppBar';
import Drawer from 'components/Drawer';
import { BookingModeSelector } from 'components/BookingModeSelector';

import i18next from 'i18next';

import { resetUser, setUser } from 'store/userSlice';
import { persistor } from 'store';
import { userSelector, user2IUser, canEditAdminSpaceId } from 'helper/security';
import { buildHotelSelectionOptions } from 'components/security/HotelSelectionInput';
import { buildMenuItemsFromOptions, FilterTextField } from 'components/form/FormInputDropdown';
import { setFilter, setBookingMode, resetFilter } from 'store/filterSlice';
import { resetReportFilters } from 'store/reportFiltersSlice';
import { resetTaskSort } from 'store/taskSortSlice';
import { filterSelector, bookingModeSelector } from 'helper/filter';

import { ME_QUERY_MINIMAL, SPACES_AND_HOTELS_QUERY, LOGOUT_MUTATION, MAINTENANCE_MODE_QUERY } from './login';
import { EHotelListerStatusType, EHotelWidgetStatusType, MeQueryQuery } from '__generated__/graphql';
import moment from 'moment';
import SimpleDialog from 'components/dialogs/Dialog';
import { Search, Task, WarningAmber, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { HOTEL_LIST_STATUS_QUERY, LIST_SPACES_PAGED_QUERY, SPACE_VIEW_QUERY } from 'pages/settings/gql';
import { isProductionRelease } from 'helper/deployment';
import { useDocumentTitle } from 'helper/usedocumenttitle';
import { EBookingMode } from 'types';
import BoLanguageSelector from 'components/BoLanguageSelector';

type TListSpacesElement = MeQueryQuery['listSpaces'][0];
type TListHotelsElement = MeQueryQuery['listHotels']['hotels'][0];
const AUTH_BOUNDARY_RESET_KEY = 'authBoundaryResetRequired';

interface LinkButtonProps {
  route: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  showWarning?: boolean;
}

const LinkButtonContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'disabled',
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  '& > a': {
    textDecoration: 'none',
    color: disabled ? `${theme.palette.disabled} !important` : theme.palette.primary.main,
    fontWeight: 'bold',
  },
  '& svg': {
    color: disabled ? theme.palette.disabled : theme.palette.primary.main,
  },
}));

/**
 * Navigation link button with active state highlighting.
 * Handles special routing logic for bookings (includes offers/widget and offers/lister).
 */
const LinkButton = ({ route, label, icon, disabled, showWarning }: LinkButtonProps) => {
  const location = useLocation();

  const CustomLink = ({ children }: any) =>
    disabled ? (
      <>{children}</>
    ) : (
      <Link to={route} style={{ textDecoration: 'none' }}>
        {children}
      </Link>
    );

  // Check if the menu item should be highlighted
  const isActive = () => {
    // For bookings route, also highlight when on offers/widget or offers/lister
    if (route === '/bookings') {
      return location.pathname.includes('/bookings') || location.pathname.includes('/offers/widget') || location.pathname.includes('/offers/lister');
    }
    // Check if pathname matches the route exactly or starts with route followed by '/'
    // This prevents /settings/space from matching /settings/spaces
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  return (
    <LinkButtonContainer disabled={disabled}>
      <CustomLink>
        <ListItemButton disabled={disabled} sx={{ backgroundColor: isActive() ? '#f3f3f3' : 'transparent' }}>
          <ListItemIcon>{icon || <DashboardIcon />}</ListItemIcon>
          <ListItemText primary={label} />
          {showWarning && <WarningAmber color="warning" />}
        </ListItemButton>
      </CustomLink>
    </LinkButtonContainer>
  );
};

/**
 * Main application layout component with sidebar navigation, filter selection, and user menu.
 * Handles authentication, space/hotel filtering, booking mode selection, and maintenance mode checks.
 * Uses two-step auth loading: ME_QUERY_MINIMAL first, then SPACES_AND_HOTELS_QUERY for non-Seminargo users.
 */
export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const documentTitle = useDocumentTitle();
  const [, setLanguageVersion] = React.useState(0);
  const user = userSelector();
  const filter = filterSelector();
  const bookingMode = bookingModeSelector();
  const location = useLocation();
  const client = useApolloClient();

  const [logoutMutation] = useMutation(LOGOUT_MUTATION);

  const [open, setOpen] = useState(true);
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const [showVersion, setShowVersion] = useState(false);

  React.useEffect(() => {
    const rerenderOnLanguageChange = () => setLanguageVersion(v => v + 1);
    i18next.on('languageChanged', rerenderOnLanguageChange);
    return () => {
      i18next.off('languageChanged', rerenderOnLanguageChange);
    };
  }, []);

  // null = spaces not loaded (Seminargo/root users), [] = loaded but empty
  const [spaces, setSpaces] = useState<TListSpacesElement[] | null>(null);
  const [hotels, setHotels] = useState<TListHotelsElement[] | null>(null);
  // For Seminargo/root users: store the currently selected space data
  const [selectedSpaceData, setSelectedSpaceData] = useState<TListSpacesElement | null>(null);

  const {
    loading,
    error,
    data: statusData,
  } = useQuery(HOTEL_LIST_STATUS_QUERY, {
    variables: {
      spaceId: (filter && filter.spaceId) || null,
    },
    skip: !filter?.spaceId,
  });

  // Check maintenance mode - use cache-first to prevent unnecessary refetches
  const { data: maintenanceData } = useQuery(MAINTENANCE_MODE_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const hasMaintenanceBypass = sessionStorage.getItem('maintenanceBypass') === 'true';

  // Redirect to login page when maintenance mode is active (unless bypassed)
  React.useEffect(() => {
    if (maintenanceData?.maintenanceMode === true && !hasMaintenanceBypass) {
      navigate('/login');
    }
  }, [maintenanceData?.maintenanceMode, hasMaintenanceBypass, navigate]);

  const filterText =
    filter && (filter.spaceId || filter.hotelId) ? (
      filter.hotelId ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <HotelIcon sx={{ flexShrink: 0 }} />
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hotels?.find(h => h.id === filter.hotelId)?.name || filter.hotelName || `Hotel #${filter.hotelId}`}
          </span>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <FolderIcon sx={{ flexShrink: 0 }} />
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {spaces?.find(s => s.id === filter.spaceId)?.name || filter.spaceName || `Space #${filter.spaceId}`}
          </span>
        </Box>
      )
    ) : null;

  /**
   * Determines current space from filter state.
   * For Seminargo/root users, uses selectedSpaceData since spaces array is null for performance.
   */
  const getCurrentSpace = () => {
    // For Seminargo/root users (spaces === null), use selectedSpaceData
    if (spaces === null) {
      return selectedSpaceData;
    }

    if (filter?.spaceId) {
      return spaces.find(s => s.id === filter.spaceId);
    } else if (filter?.hotelId) {
      const hotel = hotels?.find(h => h.id === filter.hotelId);
      // Find the full space data from spaces array using hotel's space id
      return hotel?.space ? spaces.find(s => s.id === hotel.space.id) : null;
    }
    // If no filter is set, check if user has access to spaces with both plans
    // This is important for showing the booking mode selector on initial load
    if (!filter?.spaceId && !filter?.hotelId && spaces.length > 0) {
      // Check if any accessible space has both plans
      const spaceWithBothPlans = spaces.find(s => s.hasEpilo && s.activeSEMPlan);
      if (spaceWithBothPlans) {
        return spaceWithBothPlans;
      }
      // Otherwise return the first space
      return spaces[0];
    }
    return null;
  };

  const currentSpace = getCurrentSpace();

  /**
   * Computes aggregated plan availability including child spaces.
   * If any child has Epilo/SEM, parent is considered to have it too.
   */
  const getAggregatedSpaceProps = (space: TListSpacesElement | null | undefined): { hasEpilo: boolean; hasSem: boolean } => {
    if (!space) return { hasEpilo: false, hasSem: false };

    let hasEpilo = space.hasEpilo || false;
    let hasSem = !!space.activeSEMPlan;

    // Check children recursively - if any child has Epilo/SEM, parent should have it too
    if (space.children && space.children.length > 0 && spaces) {
      for (const childRef of space.children) {
        const child = spaces.find(s => s.id === childRef.id);
        if (child) {
          const childProps = getAggregatedSpaceProps(child);
          hasEpilo = hasEpilo || childProps.hasEpilo;
          hasSem = hasSem || childProps.hasSem;
        }
      }
    }

    return { hasEpilo, hasSem };
  };

  const aggregatedSpaceProps = getAggregatedSpaceProps(currentSpace);
  const hasEpilo = aggregatedSpaceProps.hasEpilo;
  const hasSem = aggregatedSpaceProps.hasSem;
  // Show booking mode selector when both modes are available
  // OR when user is root/seminargo and no space is selected
  // OR when user is root/seminargo and a filter is set but space data hasn't loaded yet
  const hasFilterButNoSpaceData = (filter?.spaceId || filter?.hotelId) && !currentSpace;
  const shouldShowBookingModeSelector =
    (hasEpilo && hasSem) ||
    ((user?.isRoot || user?.isSeminargo) && !filter?.spaceId && !filter?.hotelId) ||
    ((user?.isRoot || user?.isSeminargo) && hasFilterButNoSpaceData);

  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);
  const filterMenuOpen = Boolean(filterMenuAnchorEl);
  const [filterPage, setFilterPage] = useState(0);
  const pageSize = 10;
  const [filterValue, setFilterValue] = useState('');
  const filterRef = React.useRef<HTMLInputElement>(null);

  const handleFilterMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterMenuAnchorEl(event.currentTarget);
    setTimeout(() => filterRef.current?.focus(), 100);
  };
  const handleFilterMenuClose = () => {
    setFilterMenuAnchorEl(null);
    setFilterValue('');
    setFilterPage(0);
  };

  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchorEl);
  const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  /**
   * Clears all Redux state and persisted storage before logout/auth boundary.
   * Prevents stale filter state from rehydrating after redirect.
   */
  const resetSessionBoundaryState = async () => {
    dispatch(resetUser());
    dispatch(resetFilter());
    dispatch(resetReportFilters());
    dispatch(resetTaskSort());
    // Reset bookingMode to default when crossing auth boundaries
    dispatch(setBookingMode(EBookingMode.EPILO));
    // Clear maintenance bypass flag across auth boundaries
    sessionStorage.removeItem('maintenanceBypass');
    sessionStorage.setItem(AUTH_BOUNDARY_RESET_KEY, '1');

    // Flush and purge persisted store so stale filter cannot rehydrate after redirect.
    try {
      await persistor.flush();
      await persistor.purge();
    } catch (error) {
      console.warn('Failed to purge persisted redux state on logout boundary', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutMutation();
    } finally {
      await resetSessionBoundaryState();
      // Clear Apollo cache to avoid stale cross-user data (logout is a user-boundary)
      try {
        await client.clearStore();
      } catch (e) {
        console.warn('Failed to clear Apollo store on logout', e);
      }
      window.location.href = '/login';
    }
  };

  // Track whether we've determined the user type and loaded spaces/hotels
  const [userTypeChecked, setUserTypeChecked] = React.useState(false);
  const [isSeminargoUserState, setIsSeminargoUserState] = React.useState<boolean | null>(null);
  // Store the user fragment from ME_QUERY_MINIMAL for later use with spaces/hotels
  const [meQueryUser, setMeQueryUser] = React.useState<any>(null);
  // Gate rendering of child routes until we know the *current* logged-in user.
  // redux-persist may hydrate a stale user/filter from a previous session; if we render children too early,
  // pages like offers can run queries with the wrong filter before ME_QUERY_* finishes.
  const [authReady, setAuthReady] = React.useState(false);
  const [spacesHotelsLoadFailed, setSpacesHotelsLoadFailed] = React.useState(false);

  const handleMeQueryError = async () => {
    await resetSessionBoundaryState();
    const currentPath = location.pathname + location.search;
    navigate(`/login?from=${encodeURIComponent(currentPath)}`, { replace: true });
  };

  /**
   * Applies one-time reset after auth boundary (logout/session-expiry).
   * Prevents transient persisted filters from leaking into new user session.
   */
  const applyPostAuthBoundaryResetIfNeeded = () => {
    if (sessionStorage.getItem(AUTH_BOUNDARY_RESET_KEY) !== '1') return;
    dispatch(resetFilter());
    dispatch(resetReportFilters());
    dispatch(resetTaskSort());
    dispatch(setBookingMode(EBookingMode.EPILO));
    sessionStorage.removeItem(AUTH_BOUNDARY_RESET_KEY);
  };

  // Step 1: Always run minimal query first to determine user type
  // This is lightweight and works for both Seminargo and regular users
  // Uses network-only to avoid stale cached user data after logout/login
  const { loading: meLoading } = useQuery(ME_QUERY_MINIMAL, {
    fetchPolicy: 'network-only',
    onError: handleMeQueryError,
    onCompleted: data => {
      if (data.me) {
        // Force a one-time reset right after a logout/session-expiry boundary.
        // This prevents transient persisted filters from leaking into the new user session.
        applyPostAuthBoundaryResetIfNeeded();
        // Determine if this is a Seminargo user (no spaceId)
        const isSeminargo = !data.me.space;
        setIsSeminargoUserState(isSeminargo);
        setUserTypeChecked(true);
        setMeQueryUser(data.me);
        setSpacesHotelsLoadFailed(false);
        setAuthReady(false);

        if (isSeminargo) {
          // For Seminargo users, we're done - just set the user
          dispatch(setUser(user2IUser(data.me)));
          setAuthReady(true);
        }
        // For non-Seminargo users, the SPACES_AND_HOTELS_QUERY will run next
      } else {
        dispatch(resetUser());
        const currentPath = location.pathname + location.search;
        navigate(`/login?from=${encodeURIComponent(currentPath)}`, { replace: true });
      }
    },
  });

  // Step 2: For non-Seminargo users, load spaces and hotels after we know the user type
  // Uses network-only to ensure fresh data after user switch
  const { loading: spacesHotelsLoading } = useQuery(SPACES_AND_HOTELS_QUERY, {
    fetchPolicy: 'network-only',
    skip: !userTypeChecked || isSeminargoUserState !== false,
    onError: err => {
      console.error('Failed to load spaces/hotels:', err);
      // Don't redirect on this error - user is already authenticated
      setSpacesHotelsLoadFailed(true);
      // As a fallback, set the user without spaces/hotels so the UI can render an error state instead of getting stuck.
      if (meQueryUser) {
        dispatch(setUser(user2IUser(meQueryUser)));
        setAuthReady(true);
      }
    },
    onCompleted: data => {
      // Use the stored user fragment from ME_QUERY_MINIMAL
      if (meQueryUser) {
        dispatch(setUser(user2IUser(meQueryUser, data.listSpaces, data.listHotels.hotels)));
      }
      setSpaces(data.listSpaces);
      setHotels(data.listHotels.hotels);
      setSpacesHotelsLoadFailed(false);
      setAuthReady(true);

      // Reset bookingMode based on current filter and available plans
      // This ensures the bookingMode is correct when switching users
      const currentFilter = filter;
      if (currentFilter) {
        let relevantSpace: TListSpacesElement | undefined;

        if (currentFilter.spaceId) {
          relevantSpace = data.listSpaces.find(s => s.id === currentFilter.spaceId);
        } else if (currentFilter.hotelId) {
          const hotel = data.listHotels.hotels.find(h => h.id === currentFilter.hotelId);
          if (hotel?.space) {
            relevantSpace = data.listSpaces.find(s => s.id === hotel.space.id);
          }
        }

        if (relevantSpace) {
          const hasEpiloInSpace = relevantSpace.hasEpilo || false;
          const hasSemInSpace = !!relevantSpace.activeSEMPlan;

          // If current bookingMode is not available in this space, reset it
          if (bookingMode === EBookingMode.EPILO && !hasEpiloInSpace && hasSemInSpace) {
            dispatch(setBookingMode(EBookingMode.SEM));
          } else if (bookingMode === EBookingMode.SEM && !hasSemInSpace && hasEpiloInSpace) {
            dispatch(setBookingMode(EBookingMode.EPILO));
          } else if (!hasEpiloInSpace && !hasSemInSpace) {
            // Default to EPILO if neither is available (shouldn't happen but safe fallback)
            dispatch(setBookingMode(EBookingMode.EPILO));
          }
        }
      }
    },
  });

  // Combined loading state
  const isLoading = meLoading || spacesHotelsLoading;

  // For JSX conditionals - derived from Redux user state
  const isSeminargoUser = user?.isSeminargo === true;

  const filterSelectOptions = spaces !== null ? buildHotelSelectionOptions(spaces, hotels || [], true) : [];

  // Check if user has only one space with one hotel
  const hasSingleHotel = spaces !== null && spaces.length === 1 && hotels !== null && hotels.length === 1;

  // Debounce search to avoid too many queries
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterValue(filterValue);
      setFilterPage(0); // Reset to first page when search changes
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValue]);

  // Paginated query for Seminargo/BO users (hotel/central users already have all spaces loaded)
  const { data: pagedData, loading: pagedLoading } = useQuery(LIST_SPACES_PAGED_QUERY, {
    variables: {
      skip: filterPage * pageSize,
      take: pageSize,
      search: debouncedFilterValue.trim().length > 0 ? debouncedFilterValue.trim() : null,
    },
    skip: !filterMenuOpen || isSeminargoUserState === false,
  });

  /**
   * Lazy query to load space data for Seminargo/root users.
   * Needed because spaces array is null for performance, but space data (hasEpilo/hasSem)
   * is required to show booking mode selector.
   */
  const [loadInitialSpaceData, { data: initialSpaceData }] = useLazyQuery(SPACE_VIEW_QUERY, {
    fetchPolicy: 'cache-first',
  });

  // Load space data on initial mount if filter is set and selectedSpaceData is not set
  React.useEffect(() => {
    const isSeminargoOrRootUser = user?.isSeminargo || user?.isRoot;
    // Get the spaceId to load: either from spaceId filter or from hotelSpaceId
    const spaceIdToLoad = filter?.spaceId || filter?.hotelSpaceId;

    // Only load for Seminargo/root users with a spaceId and no selectedSpaceData
    if (isSeminargoOrRootUser && spaceIdToLoad && !selectedSpaceData && spaces === null) {
      loadInitialSpaceData({
        variables: {
          id: spaceIdToLoad,
        },
      });
    }
  }, [user?.isSeminargo, user?.isRoot, filter?.spaceId, filter?.hotelSpaceId, selectedSpaceData, spaces, loadInitialSpaceData]);

  // Set selectedSpaceData from initialSpaceData when loaded; also hydrate filter.spaceName for Seminargo users
  React.useEffect(() => {
    if (initialSpaceData?.viewSpace && !selectedSpaceData) {
      const space = initialSpaceData.viewSpace;
      setSelectedSpaceData(space as unknown as TListSpacesElement);
      // Hydrate filter with spaceName so dropdowns show the name instead of "Space #id"
      const matchesFilter = filter?.spaceId === space.id || filter?.hotelSpaceId === space.id;
      if (matchesFilter && !filter?.spaceName) {
        dispatch(setFilter({ ...filter, spaceName: space.name }));
      }
    }
  }, [initialSpaceData, selectedSpaceData, filter, dispatch]);

  // Build options from paged data
  const pagedSpaces = pagedData?.listSpacesPaged?.spaces || [];
  const pagedSpacesList = pagedSpaces.map(item => item.space);
  let pagedHotelsList = pagedSpaces.flatMap(item => item.hotels);

  // If user isSingleSpace and only one space, show only hotels (no space entry)
  // Also filter hotels by search criteria when isSingleSpace and search is active
  const shouldShowOnlyHotels = user?.isSingleSpace && pagedSpacesList.length === 1;
  if (user?.isSingleSpace && debouncedFilterValue.trim().length > 0) {
    const searchLower = debouncedFilterValue.trim().toLowerCase();
    pagedHotelsList = pagedHotelsList.filter(
      hotel => hotel.name?.toLowerCase().includes(searchLower) || hotel.refCode?.toLowerCase().includes(searchLower),
    );
  }
  const pagedFilterSelectOptions = pagedData ? buildHotelSelectionOptions(shouldShowOnlyHotels ? [] : pagedSpacesList, pagedHotelsList, true) : [];

  // If a filter is already selected, keep only that selection visible
  const selectedSpace = filter?.spaceId ? spaces?.find(s => s.id === filter.spaceId) : null;
  const selectedHotel = filter?.hotelId ? hotels?.find(h => h.id === filter.hotelId) : null;
  const selectedHotelSpace = selectedHotel?.space ? spaces?.find(s => s.id === selectedHotel.space.id) || selectedHotel.space : null;

  /**
   * Recursively collects all child spaces of a given space.
   * Used to include child spaces in filter dropdown when parent is selected.
   */
  const collectChildSpaces = (space: TListSpacesElement): TListSpacesElement[] => {
    if (!space.children || space.children.length === 0 || !spaces) return [];
    const children: TListSpacesElement[] = [];
    for (const childRef of space.children) {
      const child = spaces.find(s => s.id === childRef.id);
      if (child) {
        children.push(child);
        children.push(...collectChildSpaces(child));
      }
    }
    return children;
  };

  // For selected space, include the space itself plus all its children
  const selectedSpaceWithChildren = selectedSpace ? [selectedSpace, ...collectChildSpaces(selectedSpace)] : [];
  const selectedSpaceIds = new Set(selectedSpaceWithChildren.map(s => s.id));

  // For selected space/hotel, also check if isSingleSpace
  const shouldShowOnlyHotelsForSelected =
    user?.isSingleSpace &&
    ((selectedSpace && spaces?.filter(s => s.id === selectedSpace.id).length === 1) ||
      (selectedHotelSpace && spaces?.filter(s => s.id === selectedHotelSpace.id).length === 1));

  const shouldKeepAllSpacesVisibleForHotelUser = isSeminargoUserState === false;
  const hotelUserSearch = filterValue.trim().toLowerCase();
  const hotelUserDropdownOptions = shouldKeepAllSpacesVisibleForHotelUser
    ? hotelUserSearch.length === 0
      ? filterSelectOptions
      : (() => {
        const matchesSearch = (option: (typeof filterSelectOptions)[number]) => {
          if (option.divider) return false;
          const searchText = (option.filterText || option.label)?.toString().toLowerCase() || '';
          return searchText.includes(hotelUserSearch);
        };
        const matchingHotelSpaceIds = new Set(
          filterSelectOptions
            .filter(option => matchesSearch(option) && option.value?.hotelId != null)
            .map(option => option.value.spaceId as number),
        );
        return filterSelectOptions.filter(option => {
          if (option.divider) return true;
          if (matchesSearch(option)) return true;
          return option.value?.hotelId == null && option.value?.spaceId != null && matchingHotelSpaceIds.has(option.value.spaceId);
        });
      })()
    : null;
  const dropdownOptions =
    hotelUserDropdownOptions ??
    (selectedSpace
      ? buildHotelSelectionOptions(
        shouldShowOnlyHotelsForSelected ? [] : selectedSpaceWithChildren,
        hotels?.filter(h => selectedSpaceIds.has(h.space.id)) || [],
        true,
      )
      : selectedHotel
        ? buildHotelSelectionOptions(
          shouldShowOnlyHotelsForSelected ? [] : selectedHotelSpace ? [selectedHotelSpace] : [],
          selectedHotel ? [selectedHotel] : [],
          true,
        )
        : pagedFilterSelectOptions);

  const isFilterDropdownLoading = pagedLoading && isSeminargoUserState !== false;

  // Handle automatic booking mode setting when only one option is available
  React.useEffect(() => {
    if (currentSpace && !shouldShowBookingModeSelector) {
      if (hasEpilo && !hasSem) {
        // Only Epilo available, set to Epilo mode
        if (bookingMode !== EBookingMode.EPILO) {
          dispatch(setBookingMode(EBookingMode.EPILO));
        }
      } else if (!hasEpilo && hasSem) {
        // Only SEM available, set to SEM mode
        if (bookingMode !== EBookingMode.SEM) {
          dispatch(setBookingMode(EBookingMode.SEM));
        }
      }
    }
  }, [currentSpace, hasEpilo, hasSem, shouldShowBookingModeSelector, bookingMode, dispatch]);

  // Handle automatic hotel filter setting when space has only one hotel
  React.useEffect(() => {
    if (filter && filter.spaceId && !filter.hotelId && hotels && hotels.length > 0) {
      // Find all hotels for the selected space
      const allAccessibleSpaceIds = new Set(spaces?.map(s => s.id) ?? [filter.spaceId]);
      const spaceHotels = hotels.filter(h => allAccessibleSpaceIds.has(h.space.id));

      // If the space has only one hotel, automatically set it as the filter
      if (spaceHotels.length === 1) {
        dispatch(
          setFilter({
            spaceId: filter.spaceId,
            hotelId: spaceHotels[0].id,
          }),
        );
      }
    }
  }, [filter, hotels, dispatch]);

  // Automatically set filter when user has only one space with one hotel
  React.useEffect(() => {
    if (hasSingleHotel && hotels && hotels.length === 1) {
      const desiredSpaceId = hotels[0].space.id;
      const desiredHotelId = hotels[0].id;
      // Always normalize to the single available hotel to avoid persisted cross-user filter leakage
      if (filter?.spaceId !== desiredSpaceId || filter?.hotelId !== desiredHotelId) {
        dispatch(
          setFilter({
            spaceId: desiredSpaceId,
            hotelId: desiredHotelId,
            hotelSpaceId: desiredSpaceId,
          }),
        );
      }
    }
  }, [hasSingleHotel, hotels, filter, dispatch]);

  // Normalize filter for hotel users after spaces/hotels have loaded:
  // - Keep any selection within the user's visible spaces/hotels (including memberships)
  // - Override only invalid/persisted cross-user selections
  React.useEffect(() => {
    if (isSeminargoUserState !== false) return;
    if (!meQueryUser?.space?.id) return;
    if (!spaces || !hotels) return;

    const userSpaceId: number = meQueryUser.space.id;
    const allowedSpaceIds = spaces.map(s => s.id);
    const hasMultipleVisibleSpaces = allowedSpaceIds.length > 1;
    const hotelsInUserScope = hotels;
    if (hotelsInUserScope.length === 0 || allowedSpaceIds.length === 0) return;

    const desired =
      hotelsInUserScope.length === 1
        ? { spaceId: hotelsInUserScope[0].space.id, hotelId: hotelsInUserScope[0].id, hotelSpaceId: hotelsInUserScope[0].space.id }
        : {
          // For users with multiple assigned administrative areas, preserve "Show all".
          spaceId: hasMultipleVisibleSpaces ? null : userSpaceId,
          hotelId: null,
          hotelSpaceId: hasMultipleVisibleSpaces ? null : userSpaceId,
        };

    // Override invalid/out-of-scope persisted filters.
    const currentSpaceId = filter?.spaceId ?? null;
    const currentHotelId = filter?.hotelId ?? null;

    const hotelIdValid = currentHotelId === null ? true : hotelsInUserScope.some(h => h.id === currentHotelId);
    const spaceIdValid =
      currentSpaceId === null
        ? hotelsInUserScope.length > 1 // multi-hotel users can have null filter (show all)
        : spaces.some(s => s.id === currentSpaceId);

    const isCurrentSpaceAllowed =
      currentSpaceId === null ? hotelsInUserScope.length > 1 && hasMultipleVisibleSpaces : allowedSpaceIds.includes(currentSpaceId);

    const shouldOverride =
      !filter ||
      !spaceIdValid ||
      !isCurrentSpaceAllowed ||
      !hotelIdValid ||
      (hotelsInUserScope.length === 1 && currentHotelId !== hotelsInUserScope[0].id);

    if (shouldOverride) {
      dispatch(setFilter(desired));
    } else if (hotelsInUserScope.length > 1) {
      // For multi-hotel spaces: keep selections in user space scope (including child spaces).
      if (!isCurrentSpaceAllowed) {
        dispatch(setFilter(desired));
      }
    }
  }, [isSeminargoUserState, meQueryUser, spaces, hotels, filter, dispatch]);

  // Handle automatic page switching when booking mode changes on booking overview pages
  React.useEffect(() => {
    if (bookingMode && location.pathname) {
      const isOnWidgetPage = location.pathname === '/offers/widget' || location.pathname.startsWith('/offers/widget/');
      const isOnListerPage = location.pathname === '/offers/lister' || location.pathname.startsWith('/offers/lister/');

      if (isOnWidgetPage && bookingMode === 'epilo') {
        navigate('/offers/lister', { replace: true });
      } else if (isOnListerPage && bookingMode === 'sem') {
        navigate('/offers/widget', { replace: true });
      }
    }
  }, [bookingMode, location.pathname, navigate]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="absolute" open={open}>
        <Toolbar
          sx={{
            pr: '24px', // keep right padding when drawer closed
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: '36px',
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            {documentTitle || 'seminargo'}
          </Typography>
          <BoLanguageSelector />
          {/*
          <IconButton color="inherit">
            <Badge badgeContent={4} color="secondary">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          */}
          {(filterSelectOptions.length > 0 || isSeminargoUser) && (
            <>
              {hasSingleHotel ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'white', px: 1 }}>
                  <HotelIcon />
                  <Typography variant="body2">{hotels![0].name}</Typography>
                </Box>
              ) : (
                <Button
                  onClick={handleFilterMenuClick}
                  color="secondary"
                  variant="text"
                  endIcon={<ArrowDropDownIcon />}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    textTransform: 'none',
                    maxWidth: '300px',
                    minWidth: 0,
                    '& .MuiButton-endIcon': {
                      marginLeft: '-2px',
                      flexShrink: 0,
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.25rem',
                    },
                  }}
                >
                  {filterText || <em>{i18next.t(user?.isSingleSpace ? 'main-filter-select-hotel' : 'main-filter-select')}</em>}
                </Button>
              )}
              {filterMenuOpen && (
                <Menu
                  anchorEl={filterMenuAnchorEl}
                  open={open}
                  onClose={handleFilterMenuClose}
                  slotProps={{
                    paper: {
                      sx: {
                        maxHeight: 480,
                        width: 360,
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        mt: 1,
                      },
                    },
                  }}
                  MenuListProps={{
                    sx: {
                      py: 1,
                    },
                  }}
                  TransitionProps={{
                    timeout: 250,
                  }}
                >
                  <FilterTextField
                    filterValue={filterValue}
                    setFilterValue={setFilterValue}
                    filterRef={filterRef}
                    placeholder={String(i18next.t('main-filter-search-placeholder'))}
                  />
                  {isFilterDropdownLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {!isFilterDropdownLoading && filterText && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <MenuItem
                        onClick={() => {
                          dispatch(setFilter({ spaceId: null, hotelId: null, hotelSpaceId: null, spaceName: null, hotelName: null }));
                          handleFilterMenuClose();
                        }}
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: '6px',
                          mx: 1,
                          mb: 0.5,
                          fontStyle: 'italic',
                          fontSize: '0.8125rem',
                          color: 'text.secondary',
                        }}
                      >
                        <em>{i18next.t('main-filter-empty')}</em>
                      </MenuItem>
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}
                  {!isFilterDropdownLoading && !filterText && <Divider sx={{ my: 1 }} />}
                  {!isFilterDropdownLoading && dropdownOptions.length === 0 && (
                    <MenuItem disabled sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                      {i18next.t('no-results')}
                    </MenuItem>
                  )}
                  {!isFilterDropdownLoading &&
                    dropdownOptions.length > 0 &&
                    buildMenuItemsFromOptions(dropdownOptions, (newFilter: any) => {
                      // Save names along with IDs for display (especially for Seminargo users)
                      let spaceName: string | null = null;
                      let hotelName: string | null = null;
                      let hotelSpaceId: number | null = null;

                      if (newFilter?.hotelId) {
                        const selectedHotelOption =
                          pagedHotelsList.find(h => h.id === newFilter.hotelId) || hotels?.find(h => h.id === newFilter.hotelId);
                        if (selectedHotelOption) {
                          hotelName = selectedHotelOption.name;
                          hotelSpaceId = selectedHotelOption.space.id;
                          const hotelSpace =
                            pagedSpacesList.find(s => s.id === selectedHotelOption.space.id) ||
                            spaces?.find(s => s.id === selectedHotelOption.space.id);
                          spaceName = hotelSpace?.name || null;
                          // Save full space data for Seminargo/root users
                          if (hotelSpace) {
                            setSelectedSpaceData(hotelSpace);
                          }
                        }
                      } else if (newFilter?.spaceId) {
                        const selectedSpaceOption =
                          pagedSpacesList.find(s => s.id === newFilter.spaceId) || spaces?.find(s => s.id === newFilter.spaceId);
                        spaceName = selectedSpaceOption?.name || null;
                        // Save full space data for Seminargo/root users
                        if (selectedSpaceOption) {
                          setSelectedSpaceData(selectedSpaceOption);
                        }
                      }

                      dispatch(
                        setFilter({
                          ...newFilter,
                          spaceName,
                          hotelName,
                          hotelSpaceId,
                        }),
                      );
                      handleFilterMenuClose();
                    })}
                  {!isFilterDropdownLoading &&
                    dropdownOptions.length > 0 &&
                    isSeminargoUser &&
                    pagedData?.listSpacesPaged?.totalCount &&
                    pagedData.listSpacesPaged.totalCount > pageSize && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            disabled={filterPage === 0}
                            onClick={() => setFilterPage(prev => Math.max(0, prev - 1))}
                            sx={{ color: 'text.secondary' }}
                          >
                            <ChevronLeft />
                          </IconButton>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            {filterPage * pageSize + 1}-{Math.min((filterPage + 1) * pageSize, pagedData.listSpacesPaged.totalCount)}{' '}
                            {i18next.t('generic-of')} {pagedData.listSpacesPaged.totalCount}
                          </Typography>
                          <IconButton
                            size="small"
                            disabled={(filterPage + 1) * pageSize >= (pagedData.listSpacesPaged.totalCount || 0)}
                            onClick={() => setFilterPage(prev => prev + 1)}
                            sx={{ color: 'text.secondary' }}
                          >
                            <ChevronRight />
                          </IconButton>
                        </Box>
                      </>
                    )}
                </Menu>
              )}
            </>
          )}
          <Button
            onClick={handleUserMenuClick}
            color="secondary"
            variant="text"
            endIcon={<ArrowDropDownIcon />}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              maxWidth: '200px',
              minWidth: 0,
              '& .MuiButton-endIcon': {
                marginLeft: '-2px',
                flexShrink: 0,
              },
            }}
          >
            {user && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  overflow: 'hidden',
                  minWidth: 0,
                }}
              >
                {user.isAdmin ? <SupervisorAccountIcon sx={{ flexShrink: 0 }} /> : <PersonIcon sx={{ flexShrink: 0 }} />}
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user.name}
                </span>
              </Box>
            )}
            {!user && '?'}
          </Button>
          {userMenuOpen && (
            <Menu anchorEl={userMenuAnchorEl} open={open} onClose={handleUserMenuClose}>
              <MenuItem onClick={() => navigate('/settings/security/me')}>
                <PersonIcon /> {i18next.t('main-usermenu-account')}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon /> {i18next.t('main-usermenu-logout')}
              </MenuItem>
            </Menu>
          )}
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: [1],
          }}
        >
          {bookingMode === EBookingMode.EPILO && (
            <img
              src={logoEpilo}
              height="30"
              alt="seminargo Logo"
              onClick={() => navigate('/offers/lister')}
              style={{ cursor: 'pointer', marginLeft: '12px' }}
            />
          )}
          {bookingMode === EBookingMode.SEM && (
            <img src={logoSem} height="50" alt="Sem Logo" onClick={() => navigate('/')} style={{ cursor: 'pointer', marginLeft: '-8px' }} />
          )}
          {bookingMode !== EBookingMode.EPILO && bookingMode !== EBookingMode.SEM && (
            <img
              src={logo}
              height="30"
              alt="seminargo Logo"
              onClick={() => navigate('/offers/lister')}
              style={{ cursor: 'pointer', marginLeft: '12px' }}
            />
          )}
        </Toolbar>
        {user && (
          <>
            <Divider />
            <List component="nav">
              {shouldShowBookingModeSelector && (
                <>
                  <BookingModeSelector />
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              {user.isSeminargo && (
                <>
                  <LinkButton route="/search" label={i18next.t('sidebar-search')} icon={<Search />} />
                  <LinkButton route="/tasks" label={i18next.t('sidebar-tasks')} icon={<Task />} />
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              <LinkButton route="/bookings" label={i18next.t('sidebar-bookings')} icon={<BookIcon />} />
              {user.isSeminargo && <LinkButton route="/offers/overflows" label={i18next.t('sidebar-overflows')} icon={<StackedBarChartIcon />} />}
              {(user.isSeminargo || user.isRoot) && (
                <LinkButton route="/offers/reports" label={i18next.t('sidebar-bericht')} icon={<ArticleIcon />} />
              )}
              <Divider sx={{ my: 1 }} />
              <ListSubheader component="div" inset>
                {i18next.t('sidebar-catalog')}
              </ListSubheader>
              {(bookingMode !== EBookingMode.EPILO || user.isRoot) && (
                <LinkButton route="/products" label={i18next.t('sidebar-products')} icon={<ShoppingCartIcon />} />
              )}
              <LinkButton
                route="/pricelists"
                label={i18next.t(
                  bookingMode === EBookingMode.SEM ? 'sidebar-pricelists' : filter?.hotelId ? 'sidebar-pricelist' : 'sidebar-pricelists',
                )}
                icon={<CreditCardIcon />}
              />
              <LinkButton route="/servicetypes" label={i18next.t('sidebar-product-bundles')} icon={<InventoryIcon />} />
              {user.isSeminargo && !isProductionRelease() && (
                <LinkButton route="/availability" label={i18next.t('sidebar-availability')} icon={<EventAvailableIcon />} />
              )}
              <Divider sx={{ my: 1 }} />
              {bookingMode === EBookingMode.SEM && (
                <>
                  <ListSubheader component="div" inset>
                    {i18next.t('sidebar-content')}
                  </ListSubheader>
                  <LinkButton route="/content/texts" label={i18next.t('sidebar-text')} icon={<TextFieldsIcon />} />
                  <LinkButton route="/content/media" label={i18next.t('sidebar-media')} icon={<PhotoLibraryIcon />} />
                  <LinkButton route="/content/templates" label={i18next.t('sidebar-templates')} icon={<ArticleIcon />} />
                  <Divider sx={{ my: 1 }} />
                </>
              )}
              <ListSubheader component="div" inset>
                {i18next.t('sidebar-settings')}
              </ListSubheader>
              {user.isSeminargo && <LinkButton route="/clients" label={i18next.t('sidebar-clients')} icon={<SupervisorAccountIcon />} />}
              {user.isSeminargo && <LinkButton route="/groups" label={i18next.t('sidebar-groups')} icon={<ApartmentIcon />} />}
              <LinkButton
                route="/settings/hotels"
                label={i18next.t('sidebar-hotels')}
                icon={<HotelIcon />}
                showWarning={
                  statusData?.listHotels?.hotels &&
                  statusData.listHotels.hotels.filter(h => {
                    if (bookingMode === EBookingMode.EPILO) {
                      return h.listerStatus !== EHotelListerStatusType.READY && h.listerStatus !== EHotelListerStatusType.INACTIVE;
                    } else {
                      return h.widgetStatus !== EHotelWidgetStatusType.READY && h.widgetStatus !== EHotelWidgetStatusType.INACTIVE;
                    }
                  }).length > 0
                }
              />
              <LinkButton route="/settings/users" label={i18next.t('sidebar-users')} icon={<GroupIcon />} />
              {(user.isAdmin || user.isRoot || user.isSeminargo) && (
                <LinkButton route="/settings/spaces" label={i18next.t('sidebar-spaces')} icon={<FolderSharedIcon />} />
              )}
              {user.isSeminargo && !isProductionRelease() && bookingMode === EBookingMode.SEM && (
                <LinkButton route="/settings/integration" label={i18next.t('sidebar-integration')} icon={<LinkIcon />} />
              )}
              {bookingMode === EBookingMode.SEM && (
                <LinkButton
                  disabled={
                    !(
                      (filter && filter.spaceId && canEditAdminSpaceId(user, filter.spaceId)) ||
                      ((!filter || !filter.spaceId) && user.isSingleAdminSpace)
                    )
                  }
                  route="/settings/space"
                  label={i18next.t('sidebar-space')}
                  icon={<PaletteIcon />}
                />
              )}
              {user.isSeminargo && (
                <LinkButton route="/administration/system" label={i18next.t('sidebar-administration')} icon={<AdminPanelSettingsIcon />} />
              )}
              <Divider sx={{ my: 1 }} />
              <LinkButton route="https://www.seminargo.com/faq" label={i18next.t('sidebar-help')} icon={<HelpCenterIcon />} />
            </List>
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                padding: '10px',
                fontSize: '11px',
                color: '#999',
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                },
              }}
            >
              <a href="https://www.seminargo.com" target="_blank">
                seminargo
              </a>{' '}
              &copy; {moment().year()}{' '}
              {process.env.REACT_APP_BRANCH && (
                <>
                  | Version:{' '}
                  <a href="#" style={{ cursor: 'pointer' }} onClick={() => setShowVersion(true)}>
                    {process.env.REACT_APP_BRANCH}
                  </a>
                </>
              )}
            </Box>
            <SimpleDialog
              open={showVersion}
              title={i18next.t('sem-version-dialog-title')}
              setOpen={setShowVersion}
              onOk={() => setShowVersion(false)}
            >
              <strong>{i18next.t('version-label-branch')}: </strong>
              {process.env.REACT_APP_BRANCH}
              <br />
              <strong>{i18next.t('version-label-build-date')}: </strong>
              {process.env.REACT_APP_BUILD_DATE}
              <br />
              <strong>{i18next.t('version-label-commit-hash')}: </strong>
              {process.env.REACT_APP_COMMIT_HASH}
              <br />
            </SimpleDialog>
          </>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: theme => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        {!authReady && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}
        {authReady && spacesHotelsLoadFailed && (
          <Box sx={{ px: 3, py: 2 }}>
            <Alert severity="error">
              {i18next.t('main-load-spaces-hotels-error')} (<code>Failed to load spaces/hotels</code>)
            </Alert>
          </Box>
        )}
        {authReady && user && <Outlet />}
        {authReady && !user && <CircularProgress />}
      </Box>
    </Box>
  );
}
